import {
  BG_STYLES,
  CaretDownIcon,
  COLORS,
  COMMON_STYLES,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import { ResponsiveContext } from "app/context/ResponsiveUIContext";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  FlatList,
  Image,
  ListRenderItem,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

import fetchFarmerFeeds from "../apis/fetchFarmerFeeds";
import CropCalendar from "../components/crop-calendar";
import Crops from "../components/crops";
import FarmReportsSection from "../components/farms";
import InsuranceSection from "../components/insurance";
import Livestocks from "../components/livestocks";
import OrdersSection from "../components/orders";
import FarmerProfileSection from "../components/profile";
import { SectionAction, SectionInfo, SectionName } from "../models/feed";
import { Farmer360HomeScreenProps, ROUTES } from "../routes/types";
import FarmerProductsSection from "../components/farmer-products";
import { trackEvent } from "../utils/tracking";
import { UserLandedOnFarmer360 } from "../utils/event";
import { useDeviceType } from "../hooks/useDeviceType";
import MenuDisplayHeader from "../components/common/MenuDisplayHeader";
import { BACK } from "../image-diagnosis/assests/images";
import { postMessageToWebView, WEBVIEW_MESSAGE_CODES } from "app/utils/common";
import getClientCode from "../utils/helpers/getClientCode";
import { NAVIGATION_FROM_APP_CODE } from "../constants/common";
import { APP_CODE_TYPE } from "../models/auth";
import { getCookieValue } from "../utils/helper";
import CreditSection from "../components/credit";

const Farmer360Details = ({ route, navigation }: Farmer360HomeScreenProps) => {
  const { mediaStyles } = useContext(ResponsiveContext);
  const flatListRef = useRef<FlatList>(null);
  let scrollRefTimerId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeMenuIndex, setActiveMenuIndex] = useState(0);

  const { farmerId, phoneNumber, name } = route.params;
  const [skeletonInfo, setSkeletonInfo] = useState<SectionInfo[]>();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [activeMenu, setActiveMenu] = useState<SectionName | null>(null);
  const [openMenuModal, setOpenMenuModal] = useState(false);
  const { isDesktop } = useDeviceType(1024);
  const [authReady, setAuthReady] = useState(false);
  const clientCode = getClientCode(
    getCookieValue(NAVIGATION_FROM_APP_CODE) as APP_CODE_TYPE,
  );
  const { width } = useWindowDimensions();

  const reload = () =>
    navigation.replace(ROUTES.FARMER_360_HOME, {
      farmerId,
      phoneNumber,
      name,
    });

  const isEditAllowed = (actions: SectionAction[]) => actions.includes("Edit");

  const fetchAndUpdateFarmerFeed = async () => {
    const resposne = await fetchFarmerFeeds();
    if (resposne) {
      setSkeletonInfo(resposne);
    }
  };

  const handleMenuPress = (index: number) => {
    if (scrollRefTimerId.current) {
      clearTimeout(scrollRefTimerId.current);
    }
    setActiveMenuIndex(index);

    if (skeletonInfo) {
      setActiveMenu(skeletonInfo[index].name);
    }

    flatListRef.current?.scrollToIndex({
      index,
      animated: true,
      viewPosition: 0.25,
    });
    scrollRefTimerId.current = setTimeout(() => {
      setActiveMenu(null);
    }, 5000);
    setOpenMenuModal(false);
  };

  const renderSection = ({ item }: { item: SectionInfo }) => {
    const commonProps = {
      name,
      phoneNumber,
      farmerId,
      reload,
      isEditAllowed: isEditAllowed(item.actions),
      highlight: false,
    };

    switch (item.name) {
      case "personal_information":
        commonProps.highlight = activeMenu === "personal_information";
        return (
          <FarmerProfileSection
            key={item.id}
            onAuthReady={() => setAuthReady(true)}
            {...commonProps}
          />
        );

      case "crop_list_section":
        commonProps.highlight = activeMenu === "crop_list_section";
        return <Crops key={item.id} {...commonProps} />;

      case "livestocks":
        return <Livestocks key={item.id} {...commonProps} />;

      case "farms":
        commonProps.highlight = activeMenu === "farms";
        return <FarmReportsSection key={item.id} {...commonProps} />;

      case "orders":
        commonProps.highlight = activeMenu === "orders";
        return <OrdersSection key={item.id} {...commonProps} />;

      case "insurance":
        commonProps.highlight = activeMenu === "insurance";
        return <InsuranceSection key={item.id} {...commonProps} />;

      case "farmer_credit":
        commonProps.highlight = activeMenu === "farmer_credit";
        return <CreditSection key={item.id} {...commonProps} />;

      case "ongoing_crops":
        commonProps.highlight = activeMenu === "ongoing_crops";
        return (
          <CropCalendar key={item.id} authReady={authReady} {...commonProps} />
        );

      case "farmer_products":
        commonProps.highlight = activeMenu === "farmer_products";
        return <FarmerProductsSection key={item.id} {...commonProps} />;
      default:
        return null;
    }
  };

  const renderMenuItem: ListRenderItem<string> = ({ item, index }) => {
    return (
      <Pressable
        style={[COMMON_STYLES["mb3/2"]]}
        onPress={() => handleMenuPress(index)}
      >
        <Text
          style={[
            TYPOGRAPHY.TEXT2,
            VARIANTS_STYLES.MEDIUM,
            index === activeMenuIndex
              ? TEXT_STYLES.textPrimary100
              : TEXT_STYLES.textNeutral90,
          ]}
        >
          {item}
        </Text>
      </Pressable>
    );
  };

  useEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <Text
          numberOfLines={1}
          ellipsizeMode="tail"
          style={{
            maxWidth: isDesktop ? "100%" : width * 0.45,
            fontSize: 16,
            fontWeight: "600",
          }}
        >
          {name || phoneNumber}
        </Text>
      ),

      headerRight: () =>
        !isDesktop && (
          <Pressable
            style={[
              COMMON_STYLES["pv3/4"],
              COMMON_STYLES["ph3/2"],
              BG_STYLES.bgPrimary100,
              COMMON_STYLES["mr0.5"],
              COMMON_STYLES.row,
              COMMON_STYLES.alignItemsCenter,
              styles["br3/2"],
            ]}
            onPress={() => setOpenMenuModal(!openMenuModal)}
          >
            <Text
              style={[
                TYPOGRAPHY.TEXT2,
                VARIANTS_STYLES.MEDIUM,
                TEXT_STYLES.textWhite,
              ]}
            >
              Quick Link
            </Text>
            <CaretDownIcon
              svgProps={{
                ...styles.caretIcon,
                style: openMenuModal && styles.rotateIcon,
              }}
              pathProps={{ stroke: COLORS.white }}
            />
          </Pressable>
        ),
    });
  }, [name, navigation, phoneNumber, openMenuModal]);

  useEffect(() => {
    fetchAndUpdateFarmerFeed().finally(() => setIsPageLoading(false));
    trackEvent(UserLandedOnFarmer360, {
      UserLandedOnFarmer360: name,
      FarmerprofileMobileNumber: phoneNumber,
    });
  }, []);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <Pressable
          style={[COMMON_STYLES["ml3/4"]]}
          onPress={() => {
            if (clientCode === "vr-service") {
              if ("ReactNativeWebView" in window) {
                postMessageToWebView(window, {
                  code: WEBVIEW_MESSAGE_CODES.BACK_CLICK,
                  message: window.location.pathname,
                });
                return true;
              } else {
                return false;
              }
            } else {
              navigation.goBack();
            }
          }}
        >
          <Image source={BACK} style={{ width: 24, height: 24 }} />
        </Pressable>
      ),
    });
  }, [navigation, clientCode]);

  return (
    <View
      style={[
        COMMON_STYLES.flex1,
        COMMON_STYLES.row,
        COMMON_STYLES.justifyCenter,
        BG_STYLES.bgWhite,
      ]}
    >
      <View
        style={[COMMON_STYLES.flex1, mediaStyles({ desktopStyle: styles.mw })]}
      >
        {isPageLoading ? (
          <Spinner />
        ) : (
          <FlatList
            ref={flatListRef}
            stickyHeaderIndices={isDesktop ? [0] : undefined}
            ListHeaderComponent={
              isDesktop && skeletonInfo ? (
                <MenuDisplayHeader
                  data={skeletonInfo.map((info) => info.metadata.menu_display)}
                  activeIndex={activeMenuIndex}
                  onMenuPress={handleMenuPress}
                  containerStyles={mediaStyles({
                    mobileStyle: COMMON_STYLES["mh1/2"],
                  })}
                />
              ) : null
            }
            data={skeletonInfo}
            renderItem={renderSection}
            keyExtractor={(item) => item.id.toString()}
            showsVerticalScrollIndicator={false}
            style={COMMON_STYLES["mt1/2"]}
          />
        )}
      </View>
      {openMenuModal && (
        <>
          <View
            style={[
              BG_STYLES.bgBlack,
              COMMON_STYLES.absolute,
              COMMON_STYLES.fullWidth,
              COMMON_STYLES.fullHeight,
              styles.menuContainer,
            ]}
          />
          <View
            style={[
              COMMON_STYLES.flex1,
              COMMON_STYLES["ph3/2"],
              COMMON_STYLES.pv1,
              COMMON_STYLES.br1,
              BG_STYLES.bgWhite,
              COMMON_STYLES.absolute,
              COMMON_STYLES.justifyCenter,
              styles.menuCard,
            ]}
          >
            <FlatList
              data={skeletonInfo?.map((info) => info.metadata.menu_display)}
              renderItem={renderMenuItem}
            />
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  mw: {
    maxWidth: 1260,
  },
  "br3/2": {
    borderRadius: 24,
  },
  caretIcon: { height: 20, width: 20 },
  rotateIcon: {
    transform: [{ rotate: "180deg" }],
  },
  menuContainer: { zIndex: 1, opacity: 0.65 },
  menuCard: { right: 8, top: 0, zIndex: 2 },
});

export default Farmer360Details;
